"use client";
import { useEffect, useState, type ReactNode } from "react";
import { emitConsentChange, grantedNow, isGranted, newConsentId, onConsentChange, openConsentPreferences, readStoredConsent, validConsent, writeStoredConsent } from "./store";
import { defaultConsentTexts } from "../texts";
import type { StoredConsent } from "../types";

/**
 * Consent API for themes, modules and core blocks (docs/cookie-consent-plan.md
 * §4.3), exported through @cms/module-api/client. Anything that loads a third
 * party must go through these so it only runs after the visitor allowed it.
 */

/** Whether a service may run now; re-renders when the visitor changes their choice. */
export function useConsent(serviceId: string): { granted: boolean; request: () => void } {
  const [granted, setGranted] = useState(false);
  useEffect(() => {
    setGranted(grantedNow(serviceId));
    return onConsentChange(() => setGranted(grantedNow(serviceId)));
  }, [serviceId]);
  return { granted, request: () => grantService(serviceId) };
}

/** "Always allow <service>": a per-service grant from an embed placeholder, logged like any other choice. */
export function grantService(serviceId: string): void {
  const config = window.__cmsConsentConfig;
  if (!config) return;
  const previous = validConsent(readStoredConsent(), config);
  const next: StoredConsent = { v: 1, id: previous?.id || newConsentId(), rev: config.revision, ts: Date.now(), c: previous?.c || {}, s: { ...(previous?.s || {}), [serviceId]: true } };
  writeStoredConsent(next, config.expiryDays);
  const body = JSON.stringify({ consentId: next.id, revision: next.rev, locale: config.locale, action: "embed", source: "embed", categories: next.c, services: next.s, path: location.pathname });
  navigator.sendBeacon?.("/api/consent", new Blob([body], { type: "application/json" }));
  document.documentElement.setAttribute("data-consent", "set");
  emitConsentChange(next);
}

/** Render children only when the service is granted; otherwise the placeholder (or nothing). */
export function ConsentGate({ service, placeholder = null, children }: { service: string; placeholder?: ReactNode; children: ReactNode }) {
  const { granted } = useConsent(service);
  return <>{granted ? children : placeholder}</>;
}

/** Load a third-party script after consent. Resolves once loaded; never resolves without consent. */
export function loadScript(serviceId: string, src: string, attrs: Record<string, string> = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const run = () => {
      const existing = document.querySelector<HTMLScriptElement>(`script[data-cms-allowed][src="${CSS.escape(src)}"]`);
      if (existing) return resolve();
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.setAttribute("data-cms-allowed", "");
      for (const [key, value] of Object.entries(attrs)) script.setAttribute(key, value);
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    };
    if (grantedNow(serviceId)) return run();
    const stop = onConsentChange(() => { if (grantedNow(serviceId)) { stop(); run(); } });
  });
}

export { onConsentChange, openConsentPreferences };

/** A link/button for theme footers that opens the cookie preferences (AP: withdrawal outside the banner). */
export function CookieSettingsLink({ className, children, locale }: { className?: string; children?: ReactNode; locale?: string }) {
  const label = useTexts(locale).floatingLabel;
  // data-cms-consent-settings lets the cookie scanner confirm a withdrawal control exists.
  return <button type="button" className={className || "cms-cookie-settings-link"} onClick={openConsentPreferences} data-cms-consent-settings=""
    style={className ? undefined : { background: "none", border: 0, padding: 0, font: "inherit", color: "inherit", textDecoration: "underline", cursor: "pointer" }}>
    {children || label}
  </button>;
}

/** Owner texts after mount, built-in defaults during server render (no hydration mismatch). */
function useTexts(locale?: string) {
  const [config, setConfig] = useState<typeof window.__cmsConsentConfig>(undefined);
  useEffect(() => setConfig(window.__cmsConsentConfig), []);
  return config && (!locale || config.locale === locale) ? config.texts : defaultConsentTexts(locale || "nl");
}

/**
 * Click-to-load placeholder for third-party embeds (video, maps, social
 * posts). Nothing is requested from the provider until the visitor clicks
 * "load and accept" or allowed the service before; the thumbnail comes from
 * our own server.
 */
export function ConsentEmbed({ service, src, title, provider, thumbnail, aspectRatio = "16 / 9", locale, allow }: {
  service: string; src: string; title: string; provider?: string; thumbnail?: string; aspectRatio?: string; locale?: string; allow?: string;
}) {
  const { granted } = useConsent(service);
  const [clicked, setClicked] = useState(false);
  const texts = useTexts(locale);
  const [config, setConfig] = useState<typeof window.__cmsConsentConfig>(undefined);
  useEffect(() => setConfig(window.__cmsConsentConfig), []);
  const serviceName = config?.categories.flatMap((category) => category.services).find((item) => item.id === service)?.name || provider || service;
  const providerName = provider || config?.categories.flatMap((category) => category.services).find((item) => item.id === service)?.provider || serviceName;
  const show = granted || clicked;
  if (show) {
    return <div className="cms-embed" style={{ position: "relative", aspectRatio }}>
      <iframe src={src} title={title} loading="lazy" allow={allow || "autoplay; encrypted-media; picture-in-picture; fullscreen"} allowFullScreen data-cms-allowed=""
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
    </div>;
  }
  return <div className="cms-embed cms-embed-placeholder" style={{ position: "relative", aspectRatio, display: "grid", placeItems: "center", background: "#1f2328", color: "#fff", overflow: "hidden", borderRadius: 8 }}>
    {thumbnail && <img src={thumbnail} alt="" loading="lazy" decoding="async" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.45 }} />}
    <div style={{ position: "relative", maxWidth: 460, padding: 20, textAlign: "center", display: "grid", gap: 10, font: "15px/1.5 system-ui, sans-serif" }}>
      <strong style={{ fontSize: 17 }}>{texts.embedTitle.replace("{service}", serviceName)}</strong>
      <span>{texts.embedBody.replace("{provider}", providerName)}</span>
      <button type="button" onClick={() => {
        setClicked(true);
        // A click on the placeholder is consent for this embed on this page view only.
        const config = window.__cmsConsentConfig;
        const stored = config ? validConsent(readStoredConsent(), config) : null;
        if (config && !isGranted(service, config, stored)) {
          const body = JSON.stringify({ consentId: stored?.id || newConsentId(), revision: config.revision, locale: config.locale, action: "embed", source: "embed", categories: stored?.c || {}, services: { [service]: true }, path: location.pathname });
          navigator.sendBeacon?.("/api/consent", new Blob([body], { type: "application/json" }));
        }
      }}
        style={{ minHeight: 44, padding: "10px 18px", borderRadius: 8, border: "2px solid #fff", background: "#fff", color: "#1f2328", font: "inherit", fontWeight: 600, cursor: "pointer" }}>
        {texts.embedLoad}
      </button>
      <button type="button" onClick={() => grantService(service)} style={{ background: "none", border: 0, color: "#fff", textDecoration: "underline", font: "inherit", cursor: "pointer", minHeight: 32 }}>
        {texts.embedAlways.replace("{service}", serviceName)}
      </button>
    </div>
  </div>;
}
